import {
  Box,
  Flex,
  Heading,
  Icon,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { formatPageList } from '@common/utils/validation';
import { TriangleAlertIcon } from 'lucide-react';
import { FC, KeyboardEvent, MouseEvent } from 'react';
import { ComplianceSuggestion } from './types';

interface DocumentSuggestionsListProps {
  suggestions: ComplianceSuggestion[];
  /**
   * Called with a row's field ids when the row is clicked/activated. The page
   * scrolls/centers the PDF on the first id. Omitted when rows aren't
   * interactive (e.g. no document selected).
   */
  onSelectFields?: (fieldIds: string[]) => void;
  /**
   * Called when a row's "Ignore" is clicked, with the suggestion to hide. The
   * page builds the mutation variables (selected document + validation ids)
   * from it. Omitted when ignoring isn't available.
   */
  onIgnore?: (suggestion: ComplianceSuggestion) => void;
  /**
   * Called when an already-ignored row's "Undo" is clicked, with the
   * suggestion to un-hide. The page re-keys the un-ignore mutation
   * (`isHidden: false`) off it. Omitted when ignoring isn't available.
   */
  onUndoIgnore?: (suggestion: ComplianceSuggestion) => void;
  /** When true, the "Ignore"/"Undo" affordances show a spinner and are disabled. */
  isIgnoring?: boolean;
}

/**
 * Right-rail card of document-level suggestions for the selected document.
 * These are validation findings presented in softer, agent-facing "suggestion"
 * framing. Each row shows a warning icon, the message, and a "Page N • Ignore"
 * subrow. Renders nothing when there are no suggestions.
 *
 * Each row with field ids is clickable: it calls `onSelectFields(row.fieldIds)`
 * so the consumer can scroll/center the PDF on the field. The per-row "Ignore"
 * link calls `onIgnore(row)` to hide the suggestion; an already-ignored
 * (agent-hidden) row renders dimmed + struck-through with an "Undo" link
 * (`onUndoIgnore(row)`) instead.
 */
export const DocumentSuggestionsList: FC<DocumentSuggestionsListProps> = ({
  suggestions,
  onSelectFields,
  onIgnore,
  onUndoIgnore,
  isIgnoring = false,
}) => {
  if (suggestions.length === 0) {
    return null;
  }

  const heading =
    suggestions.length === 1
      ? '1 document suggestion'
      : `${suggestions.length} document suggestions`;

  return (
    <Box
      bg="whiteAlpha.50"
      borderColor="whiteAlpha.100"
      borderRadius="10px"
      borderWidth={1}
      p={5}
    >
      <VStack align="stretch" spacing={2}>
        <Heading
          color="compliance.status.warning"
          fontSize="lg"
          fontWeight="normal"
          size="headline"
        >
          {heading}
        </Heading>

        <VStack align="stretch" spacing={1}>
          {suggestions.map((suggestion, index) => {
            const pageLabel = formatPageList(suggestion.pageNumbers ?? []);
            const fieldIds = suggestion.fieldIds ?? [];
            const isClickable = onSelectFields != null && fieldIds.length > 0;
            const isIgnored = suggestion.isIgnored === true;

            const handleSelect = (): void => {
              if (isClickable) {
                onSelectFields?.(fieldIds);
              }
            };

            const handleKeyDown = (event: KeyboardEvent): void => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSelect();
              }
            };

            // stopPropagation so the action doesn't also trigger the row's
            // scroll-to-field select.
            const handleIgnore = (event: MouseEvent): void => {
              event.stopPropagation();
              if (!isIgnoring) {
                onIgnore?.(suggestion);
              }
            };

            const handleUndoIgnore = (event: MouseEvent): void => {
              event.stopPropagation();
              if (!isIgnoring) {
                onUndoIgnore?.(suggestion);
              }
            };

            // An AGENT-ignored row stays visible but reads as resolved:
            // dimmed, struck-through message, muted icon, and an "Undo"
            // affordance replacing "Ignore".
            const showAction = isIgnored
              ? onUndoIgnore != null
              : onIgnore != null;

            return (
              <Flex
                key={suggestion.ruleUuid ?? `document-suggestion-${index}`}
                _hover={isClickable ? { bg: 'whiteAlpha.100' } : undefined}
                align="flex-start"
                borderRadius="6px"
                cursor={isClickable ? 'pointer' : undefined}
                gap={2}
                mx={-2}
                opacity={isIgnored ? 0.6 : undefined}
                px={2}
                py={1}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? handleSelect : undefined}
                onKeyDown={isClickable ? handleKeyDown : undefined}
              >
                <Icon
                  as={TriangleAlertIcon}
                  boxSize="16px"
                  color={
                    isIgnored
                      ? 'compliance.text.muted'
                      : 'compliance.status.warning'
                  }
                  flexShrink={0}
                  mt={1}
                />
                <Flex direction="column" gap={1} minW={0}>
                  <Text
                    color={
                      isIgnored
                        ? 'compliance.text.muted'
                        : 'compliance.text.primary'
                    }
                    fontSize="sm"
                    textDecoration={isIgnored ? 'line-through' : undefined}
                  >
                    {suggestion.message}
                  </Text>
                  <Flex
                    align="center"
                    color="whiteAlpha.600"
                    fontSize="sm"
                    gap={1.5}
                  >
                    {pageLabel && <Text>{pageLabel}</Text>}
                    {pageLabel && showAction && <Text aria-hidden>•</Text>}
                    {showAction &&
                      (isIgnoring ? (
                        <Flex align="center" gap={1.5}>
                          <Spinner size="xs" />
                          <Text>{isIgnored ? 'Undoing' : 'Ignoring'}</Text>
                        </Flex>
                      ) : isIgnored ? (
                        <Text
                          _hover={{ color: 'compliance.text.secondary' }}
                          aria-label="Undo ignore suggestion"
                          as="button"
                          cursor="pointer"
                          textDecoration="underline"
                          type="button"
                          onClick={handleUndoIgnore}
                        >
                          Undo
                        </Text>
                      ) : (
                        <Text
                          _hover={{ color: 'compliance.text.secondary' }}
                          aria-label="Ignore suggestion"
                          as="button"
                          cursor="pointer"
                          textDecoration="underline"
                          type="button"
                          onClick={handleIgnore}
                        >
                          Ignore
                        </Text>
                      ))}
                  </Flex>
                </Flex>
              </Flex>
            );
          })}
        </VStack>
      </VStack>
    </Box>
  );
};
